import { useContext } from 'react';
import { Badge } from 'react-bootstrap';
import {
  BookingRequestReadStatusEnum,
  ProviderBookingRead,
  StatusByProviderEnum,
  StatusByRepEnum,
  UserTypeEnum,
} from '../../../api';
import { SessionContext } from '../../contexts/SessionContext';

interface BookingRequestStatusBadgeProps {
  status: BookingRequestReadStatusEnum;
  bookings: ProviderBookingRead[];
  className?: string;
}

const BookingRequestStatusBadgeForProvider = ({
  status,
  bookings,
  className,
}: BookingRequestStatusBadgeProps) => {
  if (status === BookingRequestReadStatusEnum.PENDING) {
    return (
      <Badge pill bg="primary" className={className}>
        Pending Response
      </Badge>
    );
  }
  if (status === BookingRequestReadStatusEnum.RESPONDED) {
    return (
      <Badge pill bg="secondary" className={className}>
        Awaiting Confirmation
      </Badge>
    );
  }

  const anyBookingAcceptedByRep = bookings?.reduce(
    (acc, b) => acc || b.status_by_rep === StatusByRepEnum.ACCEPTED,
    false
  );
  if (status === BookingRequestReadStatusEnum.CONFIRMED && anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="success" className={className}>
        Confirmed
      </Badge>
    );
  }
  if (status === BookingRequestReadStatusEnum.CONFIRMED && !anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="danger" className={className}>
        Rejected
      </Badge>
    );
  }
  if (status === BookingRequestReadStatusEnum.CANCELED && !anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="danger" className={className}>
        Canceled
      </Badge>
    );
  }
};

const BookingRequestStatusBadgeForRep = ({
  status,
  bookings,
  className,
}: BookingRequestStatusBadgeProps) => {
  if (status === BookingRequestReadStatusEnum.PENDING) {
    return (
      <Badge pill bg="secondary" className={className}>
        Awaiting Response
      </Badge>
    );
  }

  const anyBookingAcceptedByProvider = bookings?.reduce(
    (acc, b) => acc || b.status_by_provider === StatusByProviderEnum.ACCEPTED,
    false
  );
  if (status === BookingRequestReadStatusEnum.RESPONDED && anyBookingAcceptedByProvider) {
    return (
      <Badge pill bg="primary" className={className}>
        Pending Confirmation
      </Badge>
    );
  }

  if (status === BookingRequestReadStatusEnum.RESPONDED && !anyBookingAcceptedByProvider) {
    return (
      <Badge pill bg="danger" className={className}>
        Rejected by Provider
      </Badge>
    );
  }

  const anyBookingAcceptedByRep = bookings?.reduce(
    (acc, b) => acc || b.status_by_rep === StatusByRepEnum.ACCEPTED,
    false
  );
  if (status === BookingRequestReadStatusEnum.CONFIRMED && anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="success" className={className}>
        Confirmed
      </Badge>
    );
  }
  if (status === BookingRequestReadStatusEnum.CONFIRMED && !anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="danger" className={className}>
        Rejected
      </Badge>
    );
  }
  if (status === BookingRequestReadStatusEnum.CANCELED && !anyBookingAcceptedByRep) {
    return (
      <Badge pill bg="danger" className={className}>
        Canceled
      </Badge>
    );
  }
};

export const BookingRequestStatusBadge = ({
  status,
  bookings,
  className,
}: BookingRequestStatusBadgeProps) => {
  const { user } = useContext(SessionContext);

  const userType = user?.user_type;

  if (userType === UserTypeEnum.PROVIDER) {
    return (
      <BookingRequestStatusBadgeForProvider
        status={status}
        bookings={bookings}
        className={className}
      />
    );
  }
  if (userType === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
    return (
      <BookingRequestStatusBadgeForRep status={status} bookings={bookings} className={className} />
    );
  }
  return null;
};
